import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import {
  BookIntroContentResponseModel,
  ServiceCategoriesContentResponseModel,
  SuggestionContentResponseModel,
} from '@/services/platform/api/model';
import { AppContext } from '@/lib/core/types';
import { LinksToLinkMap } from '@/lib/core/utils';
import { ProgressDots } from '@/components/shared/progressDots';
import { Suggestions } from '@/components/shared/suggestions';
import { CategoryGrid } from '@/components/shared/booking/categories/grid';
import {
  getServiceCategories,
  getSuggestions,
  getFlowByServiceCategoryId,
} from '@/services/platform/data/booking';
import { useLocaleStore } from '@/lib/core/stores';

// ── BookIntroEngine ───────────────────────────────────────────────────────────

interface BookIntroEngineProps {
  content: BookIntroContentResponseModel;
  context?: AppContext;
}

export function BookIntroEngine({ content, context }: BookIntroEngineProps) {
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [categoryItems, setCategoryItems] = useState<ServiceCategoriesContentResponseModel[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestionContentResponseModel[]>([]);

  // serviceType → route path, null means no flow found
  const [flowRouteCache, setFlowRouteCache] = useState<Record<string, string | null | undefined>>({});
  const [loadingFlows, setLoadingFlows] = useState<Record<string, boolean>>({});

  const properties = content.properties;

  // Picker stubs have empty properties — fetch full content in parallel.
  useEffect(() => {
    const stubs = properties?.suggestionsPicker ?? [];
    if (!stubs.length) return;
    getSuggestions(stubs, locale).then(setSuggestions);
  }, [properties?.suggestionsPicker, locale]);

  // Service categories live on the Book page's own serviceCategories picker.
  useEffect(() => {
    const stubs = properties?.serviceCategories ?? [];
    if (!stubs.length) return;
    getServiceCategories(stubs, locale).then(setCategoryItems);
  }, [properties?.serviceCategories, locale]);

  const handleCategoryClick = async (slug: string, categoryId?: string) => {
    setActiveSlug(slug);

    if (!categoryId) return;

    // Already resolved — navigate immediately
    if (flowRouteCache[categoryId] !== undefined) {
      if (flowRouteCache[categoryId]) {
        navigate(flowRouteCache[categoryId]!, { state: { categoryId } });
      }
      return;
    }

    setLoadingFlows(prev => ({ ...prev, [categoryId]: true }));

    const categoryFlow = await getFlowByServiceCategoryId(context?.settings, categoryId, locale);
    const routePath = categoryFlow?.route?.path;

    setFlowRouteCache(prev => ({ ...prev, [categoryId]: routePath }));
    setLoadingFlows(prev => ({ ...prev, [categoryId]: false }));

    if (routePath) navigate(routePath, { state: { categoryId } });
  };

  const handleSuggestionClick = async (item: SuggestionContentResponseModel) => {
    const p = item.properties;
    const categoryId = p?.serviceCategory?.[0]?.id;
    const venueNodeKey = p?.venue?.[0]?.id;

    const fallbackNavigate = () => {
      const link = LinksToLinkMap(p?.link ?? undefined);
      navigate(link?.href ?? item.route?.path ?? '/');
    };

    if (!categoryId) {
      fallbackNavigate();
      return;
    }

    // Already resolved — navigate immediately
    if (flowRouteCache[categoryId] !== undefined) {
      if (flowRouteCache[categoryId]) {
        navigate(flowRouteCache[categoryId]!, { state: { categoryId, venueNodeKey } });
      } else {
        fallbackNavigate();
      }
      return;
    }

    if (loadingFlows[categoryId]) return;

    setLoadingFlows(prev => ({ ...prev, [categoryId]: true }));

    const categoryFlow = await getFlowByServiceCategoryId(context?.settings, categoryId, locale);
    const routePath = categoryFlow?.route?.path;

    setFlowRouteCache(prev => ({ ...prev, [categoryId]: routePath }));
    setLoadingFlows(prev => ({ ...prev, [categoryId]: false }));

    if (routePath) {
      navigate(routePath, { state: { categoryId, venueNodeKey } });
    } else {
      fallbackNavigate();
    }
  };

  const titleHtml = properties?.title?.markup;
  const suggestionsTitleHtml = properties?.suggestionsTitle?.markup;

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      {/* Fixed header */}
      <header className="flex shrink-0 items-center justify-between px-4">
        {/* Spacer holding the header's height and the dots' right alignment — `ProgressDots` is a
            row of 1px bars, so without it the header would collapse and pull the page up. */}
        <div className="size-11" aria-hidden="true" />

        <ProgressDots step={1} />
      </header>

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-14 overflow-y-auto px-4 pt-6 pb-[calc((env(safe-area-inset-bottom)+1.5rem)+96px)]">
        {/* Title */}
        <div className="flex flex-col">
          {titleHtml && <div className="book-intro-title">{parse(titleHtml)}</div>}
        </div>

        {/* Category grid */}
        <CategoryGrid
          items={categoryItems}
          activeSlug={activeSlug}
          loadingFlows={loadingFlows}
          onItemClick={handleCategoryClick}
        />

        {/* Suggestions */}
        <Suggestions
          items={suggestions}
          titleHtml={suggestionsTitleHtml}
          onItemClick={handleSuggestionClick}
        />
      </div>
    </div>
  );
}
